import { useEffect, useState } from "react";
import api from "../api/api";
import {
  Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, Button, Box, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, Tooltip
} from "@mui/material";
import AddIcon    from "@mui/icons-material/Add";
import EditIcon   from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth } from "../components/AuthContext";

const ROWS_OPTIONS = [10, 25, 50];

export default function Departamentos() {
  const { hasPermission } = useAuth();
  const [data, setData]             = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit]     = useState(false);
  const [selected, setSelected]     = useState(null);
  const [form, setForm]             = useState({ nombre_departamento: "", descripcion: "" });
  const [pagina, setPagina]         = useState(0);
  const [rowsPorPagina, setRowsPorPagina] = useState(10);

  const cargar = async () => {
    try {
      const res = await api.get("/departamentos");
      setData(res.data);
      setPagina(0);
    } catch {
      console.error("Error loading departments");
    }
  };

  useEffect(() => { cargar(); }, []);

  const handleCreate = async () => {
    if (!form.nombre_departamento.trim()) return alert("Enter the department name");
    try {
      await api.post("/departamentos", form);
      setOpenCreate(false);
      setForm({ nombre_departamento: "", descripcion: "" });
      cargar();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleEdit = async () => {
    if (!form.nombre_departamento.trim()) return alert("Enter the department name");
    try {
      await api.put(`/departamentos/${selected.id_departamento}`, form);
      setOpenEdit(false);
      setSelected(null);
      setForm({ nombre_departamento: "", descripcion: "" });
      cargar();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this department?")) return;
    try {
      await api.delete(`/departamentos/${id}`);
      cargar();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  const openEditDialog = (d) => {
    setSelected(d);
    setForm({ nombre_departamento: d.nombre_departamento, descripcion: d.descripcion || "" });
    setOpenEdit(true);
  };

  const paginados = data.slice(pagina * rowsPorPagina, (pagina + 1) * rowsPorPagina);

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4">Departments</Typography>
        {hasPermission("DEPARTAMENTOS_CREAR") && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm({ nombre_departamento: "", descripcion: "" }); setOpenCreate(true); }}>
            Create Department
          </Button>
        )}
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow sx={{ background: "#000" }}>
            <TableCell sx={{ color: "white" }}>Name</TableCell>
            <TableCell sx={{ color: "white" }}>Description</TableCell>
            <TableCell sx={{ color: "white" }} align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginados.map(d => (
            <TableRow key={d.id_departamento} hover>
              <TableCell>{d.nombre_departamento}</TableCell>
              <TableCell>{d.descripcion || "—"}</TableCell>
              <TableCell align="right">
                {hasPermission("DEPARTAMENTOS_EDITAR") && (
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEditDialog(d)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {hasPermission("DEPARTAMENTOS_ELIMINAR") && (
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleDelete(d.id_departamento)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
          {data.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} align="center" sx={{ color: "text.secondary", py: 4 }}>
                No departments registered
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {data.length > 0 && (
        <TablePagination
          component="div"
          count={data.length}
          page={pagina}
          onPageChange={(_, p) => setPagina(p)}
          rowsPerPage={rowsPorPagina}
          onRowsPerPageChange={e => { setRowsPorPagina(parseInt(e.target.value)); setPagina(0); }}
          rowsPerPageOptions={ROWS_OPTIONS}
          labelRowsPerPage="Rows:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
          sx={{ borderTop: "1px solid", borderColor: "divider" }}
        />
      )}

      {/* Create */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Department</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Department Name" sx={{ mt: 2 }}
            value={form.nombre_departamento}
            onChange={e => setForm({ ...form, nombre_departamento: e.target.value })} />
          <TextField fullWidth label="Description (optional)" sx={{ mt: 2 }}
            value={form.descripcion}
            onChange={e => setForm({ ...form, descripcion: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Department</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Department Name" sx={{ mt: 2 }}
            value={form.nombre_departamento}
            onChange={e => setForm({ ...form, nombre_departamento: e.target.value })} />
          <TextField fullWidth label="Description (optional)" sx={{ mt: 2 }}
            value={form.descripcion}
            onChange={e => setForm({ ...form, descripcion: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit}>Update</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
